import type {
  Absence,
  AbsenceType,
  AttendanceDay,
  AttendanceEvent,
  BreakRule,
  Employee,
  Holiday,
  MonthlyClosure,
  TimeAccountAdjustment
} from "./types";

function makeId(fallback: string): string {
  return typeof globalThis.crypto?.randomUUID === "function" ? globalThis.crypto.randomUUID() : fallback;
}

export function todayKey(date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function dateTimeOn(dateKeyValue: string, time: string): string {
  return new Date(`${dateKeyValue}T${time}:00`).toISOString();
}

export function minutesBetween(startIso: string, endIso: string): number {
  return Math.max(0, Math.round((new Date(endIso).getTime() - new Date(startIso).getTime()) / 60000));
}

export function formatClock(iso: string | null): string {
  if (!iso) return "-";
  return new Intl.DateTimeFormat("de-DE", { hour: "2-digit", minute: "2-digit" }).format(new Date(iso));
}

export function formatDate(date: string): string {
  return new Intl.DateTimeFormat("de-DE", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
    year: "numeric"
  }).format(new Date(`${date}T12:00:00`));
}

export function formatMinutes(minutes: number, signed = false): string {
  const sign = minutes < 0 ? "-" : signed && minutes > 0 ? "+" : "";
  const absolute = Math.abs(minutes);
  const hours = Math.floor(absolute / 60);
  const mins = absolute % 60;
  return `${sign}${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
}

export function targetMinutesForWeekday(employee: Employee, date: string): number {
  const weekday = new Date(`${date}T12:00:00`).getDay();
  if (weekday === 1) return employee.monday_target_minutes;
  if (weekday === 2) return employee.tuesday_target_minutes;
  if (weekday === 3) return employee.wednesday_target_minutes;
  if (weekday === 4) return employee.thursday_target_minutes;
  if (weekday === 5) return employee.friday_target_minutes;
  if (weekday === 6) return employee.saturday_target_minutes;
  return employee.sunday_target_minutes;
}

export function isBusinessWeekday(date: string): boolean {
  const weekday = new Date(`${date}T12:00:00`).getDay();
  return weekday >= 1 && weekday <= 5;
}

export function businessWeekdaysInRange(startDate: string, endDate: string): number {
  const cursor = new Date(`${startDate}T12:00:00`);
  const end = new Date(`${endDate}T12:00:00`);
  if (Number.isNaN(cursor.getTime()) || Number.isNaN(end.getTime()) || cursor > end) return 0;

  let count = 0;
  while (cursor <= end) {
    if (isBusinessWeekday(todayKey(cursor))) count += 1;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
}

export function holidayForDate(holidays: Holiday[], date: string): Holiday | null {
  return holidays.find((holiday) => holiday.date === date && holiday.active && holiday.affects_working_time) ?? null;
}

export function businessDaysExcludingHolidaysInRange(startDate: string, endDate: string, holidays: Holiday[]): number {
  const cursor = new Date(`${startDate}T12:00:00`);
  const end = new Date(`${endDate}T12:00:00`);
  if (Number.isNaN(cursor.getTime()) || Number.isNaN(end.getTime()) || cursor > end) return 0;

  let count = 0;
  while (cursor <= end) {
    const date = todayKey(cursor);
    if (isBusinessWeekday(date) && !holidayForDate(holidays, date)) count += 1;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
}

export function targetMinutesForDate(employee: Employee, date: string, holidays: Holiday[]): number {
  if (holidayForDate(holidays, date)) return 0;
  return targetMinutesForWeekday(employee, date);
}

export function breakMinutesForGross(employee: Employee, grossMinutes: number): number {
  const rules = [...employee.break_rules_json].sort((a: BreakRule, b: BreakRule) => b.from_minutes - a.from_minutes);
  const matchedRule = rules.find((rule) => grossMinutes >= rule.from_minutes);
  if (matchedRule) return matchedRule.break_minutes;
  if (grossMinutes >= employee.break_deduction_threshold_minutes) return employee.default_break_minutes;
  return 0;
}

export function absencesForDate(absences: Absence[], date: string): Absence[] {
  return absences.filter((absence) => absence.status !== "cancelled" && absence.start_date <= date && absence.end_date >= date);
}

export function absenceLabel(type: AbsenceType): string {
  if (type.code === "vacation") return "Urlaub";
  if (type.code === "sick_leave") return "Krank";
  return type.name;
}

export function buildAttendanceDay(input: {
  employee: Employee;
  date: string;
  events: AttendanceEvent[];
  absences: Absence[];
  absenceTypes: AbsenceType[];
  holidays: Holiday[];
  adjustments: TimeAccountAdjustment[];
}): AttendanceDay {
  const now = new Date().toISOString();
  const dayEvents = input.events
    .filter((event) => event.employee_id === input.employee.id && todayKey(new Date(event.timestamp)) === input.date)
    .filter((event) => ["clock_in", "clock_out", "break_start", "break_end"].includes(event.event_type))
    .sort((a, b) => a.timestamp.localeCompare(b.timestamp));

  const firstClockIn = dayEvents.find((event) => event.event_type === "clock_in")?.timestamp ?? null;
  const clockOutEvents = dayEvents.filter((event) => event.event_type === "clock_out");
  const lastClockOut = clockOutEvents[clockOutEvents.length - 1]?.timestamp ?? null;
  const lastEvent = dayEvents.at(-1);
  let activeStart: string | null = null;
  let grossMinutes = 0;
  let manualInterruptionMinutes = 0;

  for (const event of dayEvents) {
    if ((event.event_type === "clock_in" || event.event_type === "break_end") && !activeStart) {
      activeStart = event.timestamp;
    }
    if ((event.event_type === "break_start" || event.event_type === "clock_out") && activeStart) {
      grossMinutes += minutesBetween(activeStart, event.timestamp);
      activeStart = null;
    }
  }

  if (activeStart && input.date === todayKey()) {
    grossMinutes += minutesBetween(activeStart, now);
  }

  for (let index = 0; index < dayEvents.length; index += 1) {
    const event = dayEvents[index];
    if (event.event_type !== "break_start") continue;
    const nextEvent = dayEvents.slice(index + 1).find((item) => item.event_type === "break_end" || item.event_type === "clock_out");
    if (nextEvent) {
      manualInterruptionMinutes += minutesBetween(event.timestamp, nextEvent.timestamp);
    }
  }

  const breakRelevantMinutes = grossMinutes + manualInterruptionMinutes;
  const requiredBreakMinutes = breakRelevantMinutes > 0 ? breakMinutesForGross(input.employee, breakRelevantMinutes) : 0;
  const deductedBreakMinutes = Math.max(0, requiredBreakMinutes - manualInterruptionMinutes);
  const netWorkMinutes = Math.max(0, grossMinutes - deductedBreakMinutes);
  const baseTargetMinutes = targetMinutesForWeekday(input.employee, input.date);
  const holiday = holidayForDate(input.holidays, input.date);
  const targetMinutes = targetMinutesForDate(input.employee, input.date, input.holidays);
  const employeeDayAbsences = absencesForDate(input.absences, input.date).filter((absence) => absence.employee_id === input.employee.id);
  const primaryAbsence =
    employeeDayAbsences
      .map((absence) => ({
        absence,
        type: input.absenceTypes.find((type) => type.id === absence.absence_type_id) ?? null
      }))
      .find((item) => item.type) ?? null;

  const rawPaidAbsenceMinutes = employeeDayAbsences.reduce((sum, absence) => {
    const absenceType = input.absenceTypes.find((type) => type.id === absence.absence_type_id);
    if (!absenceType?.counts_as_paid_time) return sum;
    if (targetMinutes <= 0) return sum;
    return sum + (absence.is_full_day ? targetMinutes : Math.min(absence.minutes_per_day ?? 0, targetMinutes));
  }, 0);
  const paidAbsenceMinutes = targetMinutes > 0 ? Math.min(rawPaidAbsenceMinutes, targetMinutes) : 0;

  const adjustmentMinutes = input.adjustments
    .filter((adjustment) => adjustment.employee_id === input.employee.id && adjustment.date === input.date)
    .reduce((sum, adjustment) => sum + adjustment.minutes, 0);

  const holidayMinutes = holiday ? baseTargetMinutes : 0;
  const creditedMinutes = netWorkMinutes + paidAbsenceMinutes + adjustmentMinutes;
  const balanceMinutes = creditedMinutes - targetMinutes;

  let dayStatus: AttendanceDay["day_status"] = "open";
  if (holiday && dayEvents.length === 0) {
    dayStatus = "holiday";
  } else if (paidAbsenceMinutes > 0 && paidAbsenceMinutes >= targetMinutes && dayEvents.length === 0) {
    dayStatus = "absence";
  } else if (firstClockIn && lastEvent?.event_type === "clock_out") {
    dayStatus = grossMinutes > 720 ? "needs_review" : "complete";
  } else if (firstClockIn && lastEvent?.event_type !== "clock_out") {
    dayStatus = input.date === todayKey() ? "open" : "missing_clock_out";
  } else if (!firstClockIn && lastClockOut) {
    dayStatus = "missing_clock_in";
  } else if (targetMinutes > 0) {
    dayStatus = "needs_review";
  }

  return {
    id: `${input.employee.id}-${input.date}`,
    employee_id: input.employee.id,
    date: input.date,
    first_clock_in: firstClockIn,
    last_clock_out: lastClockOut,
    gross_minutes: grossMinutes,
    deducted_break_minutes: deductedBreakMinutes,
    net_work_minutes: netWorkMinutes,
    target_minutes: targetMinutes,
    absence_minutes: paidAbsenceMinutes,
    holiday_minutes: holidayMinutes,
    credited_minutes: creditedMinutes,
    balance_minutes: balanceMinutes,
    day_status: dayStatus,
    absence_type_id: primaryAbsence?.absence.absence_type_id ?? null,
    absence_type_code: primaryAbsence?.type?.code ?? null,
    absence_label: primaryAbsence?.type ? absenceLabel(primaryAbsence.type) : null,
    calculation_details_json: {
      break_rule: deductedBreakMinutes,
      required_break_minutes: requiredBreakMinutes,
      holiday: holiday?.name ?? null,
      absence: primaryAbsence?.type?.name ?? null,
      adjustment_minutes: adjustmentMinutes,
      manual_interruption_minutes: manualInterruptionMinutes
    },
    approved_by_user_id: null,
    approved_at: null,
    created_at: now,
    updated_at: now
  };
}

export function monthDates(year: number, month: number): string[] {
  const dates: string[] = [];
  const cursor = new Date(year, month - 1, 1, 12, 0, 0);
  while (cursor.getMonth() === month - 1) {
    dates.push(todayKey(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
}

export function buildMonthlyClosure(input: {
  employee: Employee;
  year: number;
  month: number;
  days: AttendanceDay[];
  absenceTypes: AbsenceType[];
  absences: Absence[];
  carryoverPreviousMinutes: number;
  status?: MonthlyClosure["status"];
}): MonthlyClosure {
  const now = new Date().toISOString();
  const monthStart = `${input.year}-${String(input.month).padStart(2, "0")}-01`;
  const monthEnd = monthDates(input.year, input.month).at(-1) ?? monthStart;
  const employeeAbsences = input.absences.filter(
    (absence) => absence.employee_id === input.employee.id && absence.status !== "cancelled" && absence.start_date <= monthEnd && absence.end_date >= monthStart
  );
  const vacationType = input.absenceTypes.find((type) => type.code === "vacation");
  const sickType = input.absenceTypes.find((type) => type.code === "sick_leave");
  const vacationMinutes = input.days.reduce((sum, day) => {
    const hasVacation = employeeAbsences.some((absence) => absence.absence_type_id === vacationType?.id && absence.start_date <= day.date && absence.end_date >= day.date);
    return sum + (hasVacation ? day.absence_minutes : 0);
  }, 0);
  const sickMinutes = input.days.reduce((sum, day) => {
    const hasSick = employeeAbsences.some((absence) => absence.absence_type_id === sickType?.id && absence.start_date <= day.date && absence.end_date >= day.date);
    return sum + (hasSick ? day.absence_minutes : 0);
  }, 0);
  const targetMinutes = input.days.reduce((sum, day) => sum + day.target_minutes, 0);
  const workedMinutes = input.days.reduce((sum, day) => sum + day.net_work_minutes, 0);
  const holidayMinutes = input.days.reduce((sum, day) => sum + day.holiday_minutes, 0);
  const otherAbsenceMinutes = Math.max(0, input.days.reduce((sum, day) => sum + day.absence_minutes, 0) - vacationMinutes - sickMinutes);
  const monthBalanceMinutes = input.days.reduce((sum, day) => sum + day.balance_minutes, 0);
  const needsReview = input.days.some((day) => ["missing_clock_in", "missing_clock_out", "needs_review"].includes(day.day_status));

  return {
    id: makeId(`${input.employee.id}-${input.year}-${input.month}`),
    employee_id: input.employee.id,
    year: input.year,
    month: input.month,
    target_minutes: targetMinutes,
    worked_minutes: workedMinutes,
    vacation_minutes: vacationMinutes,
    sick_minutes: sickMinutes,
    holiday_minutes: holidayMinutes,
    other_absence_minutes: otherAbsenceMinutes,
    correction_minutes: 0,
    month_balance_minutes: monthBalanceMinutes,
    carryover_previous_minutes: input.carryoverPreviousMinutes,
    carryover_new_minutes: input.carryoverPreviousMinutes + monthBalanceMinutes,
    status: input.status ?? (needsReview ? "needs_review" : "open"),
    approved_by_user_id: null,
    approved_at: null,
    locked_at: null,
    reopened_at: null,
    comment: needsReview ? "Offene Prüfpunkte vorhanden" : null,
    created_at: now,
    updated_at: now
  };
}
